import {
  createRouter,
  parseSearchWith,
  RouterProvider,
  stringifySearchWith,
} from '@tanstack/react-router';
import { Provider as JotaiProvider } from 'jotai';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { TooltipProvider } from './components/ui/tooltip';
import { AuthProvider } from './providers/auth-provider';
import { routeTree } from './routeTree.gen';
import { store } from './stores/auth.store';
import './globals.css';

/**
 * Renders a search value into the URL.
 *
 * A list of strings goes out comma-separated — `?state=active,disabled` rather
 * than the router's default `?state=%5B%22active%22%2C%22disabled%22%5D`. The
 * admin filter lists are the only search params in the app that hold arrays,
 * and their URLs get pasted into tickets and shared between administrators, so
 * legibility is worth the one-line override.
 *
 * It has to live here rather than in each route: the router re-serialises the
 * URL from whatever `validateSearch` returned, so a route that comma-joins on
 * the way into `navigate` has its work undone on the next render.
 *
 * Parsing is unchanged. `JSON.parse` fails on `active,disabled` and the router
 * falls back to the raw string, which each route's validator splits — so both
 * encodings still round-trip and older links keep working.
 */
function stringifySearchValue(value: unknown): string {
  if (Array.isArray(value) && value.every((entry) => typeof entry === 'string')) {
    return value.join(',');
  }
  return JSON.stringify(value);
}

const router = createRouter({
  routeTree,
  parseSearch: parseSearchWith(JSON.parse),
  stringifySearch: stringifySearchWith(stringifySearchValue, JSON.parse),
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

createRoot(rootElement).render(
  <StrictMode>
    <JotaiProvider store={store}>
      <TooltipProvider>
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
      </TooltipProvider>
    </JotaiProvider>
  </StrictMode>,
);
